import { useState, useEffect, useCallback } from 'react';
import { getPageConfig, PageConfigView } from '../api/Navigation/moduleConfig';
import { PROFILE_TAB_MODULE_ID } from '../components/layout/navigationModules';

/**
 * Hook that fetches profile tab configuration for the current user's company.
 * Returns `isTabEnabled(tabId)` to check if a specific profile tab should be shown.
 *
 * Behavior:
 * - Empty config (no overrides) = all tabs enabled (backward compatible)
 * - "personal" tab is always enabled as a safety net
 */
export function useProfileTabConfig() {
  const [tabConfig, setTabConfig] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const allPages: PageConfigView[] = await getPageConfig();
      if (cancelled) return;

      const config: Record<string, boolean> = {};
      allPages
        .filter(pc => pc.moduleId === PROFILE_TAB_MODULE_ID)
        .forEach(pc => {
          config[pc.pagePath] = pc.isEnabled;
        });

      setTabConfig(config);
      setLoaded(true);
    };

    load();
    return () => { cancelled = true; };
  }, []);

  const isTabEnabled = useCallback(
    (tabId: string): boolean => {
      // Personal tab is always enabled
      if (tabId === 'personal') return true;
      // If no config loaded yet or no overrides exist, show all tabs (backward compatible)
      if (!loaded || Object.keys(tabConfig).length === 0) return true;
      // Default to enabled if not explicitly configured
      return tabConfig[tabId] ?? true;
    },
    [tabConfig, loaded]
  );

  return { isTabEnabled, loaded };
}
